import React from "react";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import FormField from "@cloudscape-design/components/form-field";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Table from "@cloudscape-design/components/table";
import Tabs from "@cloudscape-design/components/tabs";
import Toggle from "@cloudscape-design/components/toggle";
import OtpRouteEditor, { getRoutingError, normalizeRouting } from "./OtpRouteEditor";
import SmsRoutingLogs from "./SmsRoutingLogs";

const errorText = (error) => error?.text || error?.message || String(error || "Request failed");

const emptyCredentials = (driver) => {
	if (!driver) return {};
	if (driver.regions) return driver.regions.reduce((all, region) => ({ ...all, [region]: driver.fields.reduce((fields, field) => ({ ...fields, [field.key]: "" }), {}) }), {});
	return driver.fields.reduce((fields, field) => ({ ...fields, [field.key]: "" }), {});
};

class SmsRouting extends React.Component {
	state = { tab: "providers", loaded: false, error: "", drivers: [], providers: [], routes: [], countries: [], settings: { fallbackDelay: 15 }, savingSettings: false, providerModal: null, routeModal: null, removeRouteModal: null };
	componentDidMount() {
		this.load();
	}
	request = (type, method, data) => this.props.Interface.request(type, { class: "data", module: "smsRouting", method }, data || {});
	load = () => {
		this.setState({ loaded: false, error: "" });
		Promise.all([this.request("get", "drivers"), this.request("get", "countries"), this.request("get", "providers"), this.request("get", "routes"), this.request("get", "settings")])
			.then(([drivers, countries, providers, routes, settings]) => this.setState({ loaded: true, drivers: drivers.items || [], countries: countries.items || [], providers: providers.items || [], routes: routes.items || [], settings }))
			.catch((error) => this.setState({ loaded: true, error: errorText(error) }));
	};
	driverById = (id) => this.state.drivers.find((driver) => driver.id === id) || null;
	openProvider = (provider) => {
		this.setState({ providerModal: { ...provider, credentials: {}, loading: true, saving: false } });
		this.request("get", "provider", { id: provider.id })
			.then((item) => this.setState({ providerModal: { ...item, credentials: Object.keys(item.credentials || {}).length ? item.credentials : emptyCredentials(this.driverById(item.driver)), loading: false, saving: false } }))
			.catch((error) => this.setState({ providerModal: null, error: errorText(error) }));
	};
	setCredential = (key, value, region) =>
		this.setState((state) => {
			const credentials = { ...state.providerModal.credentials };
			if (region) credentials[region] = { ...(credentials[region] || {}), [key]: value };
			else credentials[key] = value;
			return { providerModal: { ...state.providerModal, credentials } };
		});
	saveProvider = () => {
		const modal = this.state.providerModal;
		this.setState({ providerModal: { ...modal, saving: true } });
		this.request("set", "saveProvider", { id: modal.id, name: modal.name.trim(), driver: modal.driver, allowManualFallback: modal.allowManualFallback, ...(modal.driver === "aws" || modal.credentialsReadOnly ? {} : { credentials: modal.credentials }) })
			.then(() => {
				this.props.Interface.addNoty("Success", "OTP provider saved");
				this.setState({ providerModal: null });
				this.load();
			})
			.catch((error) => this.setState({ error: errorText(error), providerModal: { ...modal, saving: false } }));
	};
	saveSettings = () => {
		this.setState({ savingSettings: true });
		this.request("set", "saveSettings", { fallbackDelay: Number(this.state.settings.fallbackDelay) })
			.then((settings) => {
				this.props.Interface.addNoty("Success", "OTP fallback delay saved");
				this.setState({ settings, savingSettings: false });
			})
			.catch((error) => this.setState({ error: errorText(error), savingSettings: false }));
	};
	openRoute = (route) => this.setState({ routeModal: { country: route ? { value: route.country, label: route.countryLabel } : null, lockedCountry: Boolean(route), routing: normalizeRouting(route?.routing), saving: false, error: "" } });
	saveRoute = () => {
		const modal = this.state.routeModal;
		if (modal.settingsOpen || modal.unfinishedCondition) return;
		const error = !modal.country ? "Choose a destination country before saving." : getRoutingError(modal.routing, this.state.providers);
		if (error) return this.setState({ routeModal: { ...modal, error } });
		this.setState({ routeModal: { ...modal, saving: true, error: "" } });
		this.request("set", "saveRoute", { country: modal.country.value, routing: modal.routing })
			.then(() => {
				this.props.Interface.addNoty("Success", "OTP country route saved");
				this.setState({ routeModal: null });
				this.load();
			})
			.catch((error) => this.setState({ routeModal: { ...modal, saving: false, error: errorText(error) } }));
	};
	deleteRoute = () => {
		const modal = this.state.removeRouteModal;
		this.setState({ removeRouteModal: { ...modal, saving: true } });
		this.request("set", "deleteRoute", { country: modal.country })
			.then(() => {
				this.setState({ removeRouteModal: null });
				this.load();
			})
			.catch((error) => this.setState({ error: errorText(error), removeRouteModal: { ...modal, saving: false } }));
	};
	renderCredentialFields = (driver, credentials) => {
		if (!driver || !driver.fields.length) return <Box>This driver uses the runtime AWS credentials.</Box>;
		const renderField = (field, region) => {
			const source = region ? credentials[region] || {} : credentials;
			return (
				<FormField key={`${region || "root"}-${field.key}`} label={`${region ? `${region.toUpperCase()} ` : ""}${field.label}`}>
					<Input type={field.secret ? "password" : "text"} value={String(source[field.key] ?? "")} placeholder={field.optional ? "Optional" : "Required"} onChange={({ detail }) => this.setCredential(field.key, detail.value, region)} />
				</FormField>
			);
		};
		return (
			<SpaceBetween size="s">
				{driver.regions
					? driver.regions.map((region) => (
							<Container key={region} header={<Header variant="h3">{region.toUpperCase()}</Header>}>
								{driver.fields.map((field) => renderField(field, region))}
							</Container>
					  ))
					: driver.fields.map((field) => renderField(field))}
			</SpaceBetween>
		);
	};
	renderProviders = () => (
		<SpaceBetween size="m">
			<Container header={<Header variant="h2">Fallback</Header>}>
				<FormField label="OTP fallback delay" description="Seconds before manual switching becomes available after any provider, when enabled in the current provider settings and a next provider is available.">
					<div style={{ display: "flex", gap: 8, maxWidth: 360 }}>
						<Input type="number" value={String(this.state.settings.fallbackDelay)} onChange={({ detail }) => this.setState({ settings: { fallbackDelay: detail.value } })} />
						<Button variant="primary" loading={this.state.savingSettings} onClick={this.saveSettings}>
							Save
						</Button>
					</div>
				</FormField>
			</Container>
			<Table
				trackBy="id"
				loading={!this.state.loaded}
				items={this.state.providers}
				empty="No OTP providers"
				header={<Header description="AWS SNS uses server-side AWS SDK credentials; other providers use this registry.">Providers ({this.state.providers.length})</Header>}
				columnDefinitions={[
					{ id: "name", header: "Name", cell: (item) => item.name },
					{ id: "driver", header: "Driver", cell: (item) => item.driverLabel },
					{ id: "credentials", header: "Credentials", cell: (item) => (item.driver === "aws" ? "AWS SDK (server)" : item.usesBackendConfig ? "Not configured" : "Configured") },
					{ id: "manualFallback", header: "Manual fallback", cell: (item) => (item.allowManualFallback ? "Enabled" : "Disabled") },
					{ id: "actions", header: "Actions", cell: (item) => <Button onClick={() => this.openProvider(item)}>Edit</Button> },
				]}
			/>
		</SpaceBetween>
	);
	renderCountries = () => (
		<Table
			trackBy="country"
			loading={!this.state.loaded}
			items={this.state.routes}
			empty="No routes. All valid OTP requests are silently suppressed until a country or DEFAULT route is added."
			header={
				<Header
					description="The phone country selects a route. DEFAULT is used only for valid numbers without an explicit country route."
					actions={
						<Button variant="primary" iconName="add-plus" onClick={() => this.openRoute(null)}>
							Add country
						</Button>
					}>
					Countries ({this.state.routes.length})
				</Header>
			}
			columnDefinitions={[
				{ id: "country", header: "Country", cell: (item) => item.countryLabel },
				{ id: "cohorts", header: "Cohorts", cell: (item) => item.routing?.cohorts?.length || 0 },
				{ id: "cascade", header: "Routes", cell: (item) => (item.routing?.cohorts || []).map((cohort) => `${cohort.name}: ${cohort.providers?.length || 0}`).join("; ") || "Silent suppression" },
				{
					id: "actions",
					header: "Actions",
					cell: (item) => (
						<SpaceBetween direction="horizontal" size="xs">
							<Button onClick={() => this.openRoute(item)}>Edit</Button>
							<Button onClick={() => this.setState({ removeRouteModal: item })}>Remove</Button>
						</SpaceBetween>
					),
				},
			]}
		/>
	);
	renderProviderModal = () => {
		const modal = this.state.providerModal;
		if (!modal) return null;
		return (
			<Modal
				visible
				size="medium"
				header="Edit OTP provider"
				onDismiss={() => this.setState({ providerModal: null })}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button onClick={() => this.setState({ providerModal: null })}>Cancel</Button>
							<Button variant="primary" disabled={modal.loading || !modal.name.trim()} loading={modal.saving} onClick={this.saveProvider}>
								Save
							</Button>
						</SpaceBetween>
					</Box>
				}>
				{modal.loading ? (
					<Box textAlign="center">
						<Spinner size="large" />
					</Box>
				) : (
					<SpaceBetween size="m">
						<FormField label="Name">
							<Input value={modal.name} onChange={({ detail }) => this.setState({ providerModal: { ...modal, name: detail.value } })} />
						</FormField>
						<FormField label="Driver">
							<Input disabled value={modal.driver} />
						</FormField>
						<FormField label="Manual fallback" description="Applies after this provider in all countries and cohorts. The next provider does not need this setting enabled. Automatic fallback on API errors is unaffected.">
							<Toggle checked={modal.allowManualFallback} onChange={({ detail }) => this.setState({ providerModal: { ...modal, allowManualFallback: detail.checked } })}>
								Allow the user to switch to the next available provider
							</Toggle>
						</FormField>
						{modal.driver === "aws" || modal.credentialsReadOnly ? (
							<Alert type="info" header="Server-side AWS credentials">
								AWS SNS uses the AWS SDK credential chain on the server (environment, AWS profile or IAM role). Database keys are not required. Credentials cannot be viewed or changed in XPanel; saving the provider name does not change them.
							</Alert>
						) : (
							<SpaceBetween size="s">
								{this.renderCredentialFields(this.driverById(modal.driver), modal.credentials)}
								<Box color="text-body-secondary">Masked or empty secret fields keep their current saved value.</Box>
							</SpaceBetween>
						)}
					</SpaceBetween>
				)}
			</Modal>
		);
	};
	renderRouteModal = () => {
		const modal = this.state.routeModal;
		if (!modal) return null;
		const updateDraft = (patch) => this.setState((state) => ({ routeModal: { ...state.routeModal, ...patch, error: "" } }));
		return (
			<Modal
				visible
				size="large"
				header={modal.lockedCountry ? "Edit country route" : "Add country route"}
				onDismiss={() => !modal.saving && !modal.settingsOpen && this.setState({ routeModal: null })}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button disabled={modal.saving || modal.settingsOpen} onClick={() => this.setState({ routeModal: null })}>
								Cancel
							</Button>
							<Button variant="primary" loading={modal.saving} disabled={modal.settingsOpen || modal.unfinishedCondition} onClick={this.saveRoute}>
								Save route
							</Button>
						</SpaceBetween>
					</Box>
				}>
				<SpaceBetween size="m">
					{modal.error ? (
						<Alert type="error" header="Route could not be saved">
							{modal.error}
						</Alert>
					) : null}
					<fieldset disabled={modal.saving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
						<OtpRouteEditor country={modal.country} lockedCountry={modal.lockedCountry} routing={modal.routing} countries={this.state.countries} providers={this.state.providers} routes={this.state.routes} fallbackDelay={this.state.settings.fallbackDelay} onCountryChange={(country) => updateDraft({ country })} onChange={(routing) => updateDraft({ routing })} onDialogChange={(settingsOpen) => updateDraft({ settingsOpen })} onConditionDraftChange={(unfinishedCondition) => updateDraft({ unfinishedCondition })} />
					</fieldset>
				</SpaceBetween>
			</Modal>
		);
	};
	render() {
		return (
			<SpaceBetween size="m">
				<Header
					variant="h1"
					description="Configure flat OTP provider cascades by phone country and cohort."
					actions={
						<Button disabled={!this.state.loaded} onClick={this.load}>
							Reload
						</Button>
					}>
					OTP Routing
				</Header>
				{this.state.error ? (
					<Alert type="error" dismissible onDismiss={() => this.setState({ error: "" })}>
						{this.state.error}
					</Alert>
				) : null}
				<Tabs
					activeTabId={this.state.tab}
					onChange={({ detail }) => this.setState({ tab: detail.activeTabId })}
					tabs={[
						{ id: "providers", label: "Providers", content: this.renderProviders() },
						{ id: "countries", label: "Countries", content: this.renderCountries() },
						{ id: "logs", label: "Logs", content: this.state.tab === "logs" ? <SmsRoutingLogs request={this.request} /> : null },
					]}
				/>
				{this.renderProviderModal()}
				{this.renderRouteModal()}
				{this.state.removeRouteModal ? (
					<Modal
						visible
						header="Remove country route"
						onDismiss={() => this.setState({ removeRouteModal: null })}
						footer={
							<Box float="right">
								<SpaceBetween direction="horizontal" size="xs">
									<Button onClick={() => this.setState({ removeRouteModal: null })}>Cancel</Button>
									<Button variant="primary" loading={this.state.removeRouteModal.saving} onClick={this.deleteRoute}>
										Remove
									</Button>
								</SpaceBetween>
							</Box>
						}>
						Remove {this.state.removeRouteModal.countryLabel}? Valid numbers will use DEFAULT if configured; otherwise requests will be silently suppressed.
					</Modal>
				) : null}
			</SpaceBetween>
		);
	}
}

export default SmsRouting;
